import { tableColumnsConfig, type ColumnKey, type TableKey } from "#/config/tableColumnsConfig";
import { useClient } from "#/hook/useClient";
import { getVisibleColumnKeys } from "#/utils/columnVisibilityUtils";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface ColumnRenderer<Row> {
  /** Cell content for one row. */
  cell: (row: Row) => ReactNode;
  /** Tailwind classes for every cell of this column (widths, alignment). */
  cellClassName?: string;
  /** Column can be sorted on the server; the key sent as `sortBy`. Omitted = not sortable. */
  sortKey?: string;
}

export interface VisibleColumn<Row> extends ColumnRenderer<Row> {
  key: string;
  /** Translated header. */
  label: string;
}

interface Options<T extends TableKey> {
  /**
   * Columns this particular view does not have, e.g. the "linked to" column on
   * the Not Linked tab of RFID tags. They stay in the admin config.
   */
  exclude?: readonly ColumnKey<T>[];
  /**
   * Number of non-data columns the table prepends (1 for SelectableTable's
   * checkbox column, 0 for BaseTable). Sort indices from the table include it.
   */
  leadingColumns?: number;
}

/**
 * Builds a list page's table from the shared column config and the client's
 * "Column Visibility" setting, so header, cells, sortable columns and sort
 * keys all come from one filtered list and can never drift.
 *
 * `renderers` must cover every column key of the table (compile-time checked)
 * and nothing else. The Actions column is not part of this: the page appends
 * its own header and cell after `headers` / `cells(row)`.
 */
export function useTableColumns<T extends TableKey, Row>(
  tableKey: T,
  renderers: Record<ColumnKey<T>, ColumnRenderer<Row>>,
  options: Options<T> = {},
) {
  const { t } = useTranslation();
  const { client } = useClient();
  const { exclude, leadingColumns = 0 } = options;
  const excluded = useMemo(() => new Set<string>(exclude ?? []), [exclude]);

  const visible = useMemo<VisibleColumn<Row>[]>(() => {
    const visibleKeys = new Set(getVisibleColumnKeys(tableKey, client));
    // The config is `as const`, so `columns` is a tuple per table; widen for iteration.
    const columns: readonly { key: string; label: string }[] = tableColumnsConfig[tableKey].columns;
    const byKey = renderers as Record<string, ColumnRenderer<Row>>;
    return columns
      .filter((column) => visibleKeys.has(column.key) && !excluded.has(column.key))
      .map((column) => ({
        key: column.key,
        label: t(column.label),
        ...byKey[column.key],
      }));
  }, [tableKey, client, excluded, renderers, t]);

  const headers = visible.map((column) => column.label);

  /** Table indices (including leading columns) that have a server sort key. */
  const sortableColumns = visible
    .map((column, index) => (column.sortKey ? index + leadingColumns : null))
    .filter((index): index is number => index !== null);

  /** Server sort key for a table index, or undefined when that column is not sortable. */
  const sortKeyAt = (tableIndex: number): string | undefined =>
    visible[tableIndex - leadingColumns]?.sortKey;

  /** Table index of a column key (including leading columns), for defaultSort. */
  const indexOf = (key: ColumnKey<T>): number | undefined => {
    const position = visible.findIndex((column) => column.key === key);
    return position === -1 ? undefined : position + leadingColumns;
  };

  const cells = (row: Row): ReactNode[] => visible.map((column) => column.cell(row));

  return { visible, headers, sortableColumns, sortKeyAt, indexOf, cells };
}
